import React, { Fragment, useEffect, useMemo, useRef, useState } from "react"
import classNames from "../../../utils/classNames"
import { Menu } from "@headlessui/react"
import { isMobile } from "react-device-detect"
import { Transition } from "semantic-ui-react"
import { createPortal } from "react-dom"

export interface DropdownOption {
  icon?: any // TODO: component definition
  label: string | JSX.Element
  onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>) => void
  disabled?: boolean
}

interface ComponentProps {
  stopPropagation?: boolean
  children: React.ReactNode
  options: Array<DropdownOption>
  autoWidth?: boolean
  preventCloseOnClick?: boolean
  isMenuFixed?: boolean
  disabled?: boolean
  placement?: "right" | "left"
  verticalPlacement?: "top" | "bottom"
  zIndex?: string
  dataTestId?: string
  minWidth?: number
}

const DropdownButton = (props: ComponentProps) => {
  return (
    <>
      {props.isMenuFixed ? (
        <>
          <FixedMenu
            options={props.options}
            preventCloseOnClick={props.preventCloseOnClick}
            children={props.children}
            disabled={props.disabled}
            autoWidth={props.autoWidth}
            placement={props.placement}
            dataTestId={props.dataTestId}
            verticalPlacement={props.verticalPlacement}
            zIndex={props.zIndex}
            minWidth={props.minWidth}
          />
        </>
      ) : (
        <AbsoluteMenu
          options={props.options}
          children={props.children}
          disabled={props.disabled}
          autoWidth={props.autoWidth}
          placement={props.placement}
          minWidth={props.minWidth}
        />
      )}
    </>
  )
}

interface AbsoluteMenuProps {
  options: DropdownOption[]
  children
  disabled
  autoWidth
  placement
  minWidth?: number
}

const AbsoluteMenu = ({ options, children, disabled, autoWidth, placement, minWidth = 200 }: AbsoluteMenuProps) => {
  const [open, setOpen] = useState(false)
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({})
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (buttonRef.current) {
      const contentWidth = autoWidth ? buttonRef.current.offsetWidth : minWidth
      const dropdownWidth = Math.max(minWidth, contentWidth)

      const buttonRect = buttonRef.current?.getBoundingClientRect()

      let styles: React.CSSProperties = {
        top: buttonRect.height + 10,
        width: dropdownWidth,
        right: !placement || placement === "right" ? 0 : undefined,
        left: placement === "left" ? 0 : undefined,
      }

      setDropdownStyles(styles)
    }
  }, [autoWidth, open])

  return (
    <Menu as="div" ref={containerRef} className="relative inline-block text-left">
      <div>
        <Menu.Button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={classNames(
            disabled ? "pointer-events-none opacity-30" : "cursor-pointer",
            "inline-flex justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
          )}
        >
          {children}
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          onBlur={() => {
            setOpen(false)
          }}
          ref={menuRef}
          id="item_menu"
          style={dropdownStyles}
          className={classNames(
            "absolute z-50 top-2 border-gray-300 rounded-lg bg-white shadow-lg ring-1 ring-gray-200 ring-opacity-50 focus:outline-none p-0.5",
          )}
        >
          <>
            {options.map((option, index) => (
              <Menu.Item key={index}>
                {option.label === "_divider_" ? (
                  <div className="my-1 border-b border-gray-200 w-full" key={index} />
                ) : (
                  <div
                    onClick={option.onClick}
                    className={classNames(
                      option.disabled
                        ? "text-gray-300 pointer-events-none"
                        : "hover:bg-gray-100 text-muted hover:cursor-pointer transition-colors duration-75",
                      "group flex w-full items-center rounded-md px-2 py-2 whitespace-nowrap transition-colors",
                    )}
                  >
                    {option.icon && (
                      <span className="mr-2">
                        <option.icon className="w-4 h-4" />
                      </span>
                    )}
                    <span className="w-full group-hover:text-black text-gray-600">{option.label}</span>
                  </div>
                )}
              </Menu.Item>
            ))}
          </>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

interface FixedMenuOption {
  label: string
  onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>) => void
  disabled?: boolean
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

interface FixedMenuProps {
  options: FixedMenuOption[]
  preventCloseOnClick?: boolean
  children: React.ReactNode
  disabled?: boolean
  autoWidth?: boolean
  placement?: "left" | "right"
  verticalPlacement?: "top" | "bottom"
  zIndex?: string
  dataTestId?: string
  minWidth?: number
}

const FixedMenu = ({
  options,
  preventCloseOnClick,
  children,
  disabled,
  autoWidth = true,
  placement,
  verticalPlacement,
  zIndex,
  dataTestId,
  minWidth = 50,
}: FixedMenuProps) => {
  const [open, setOpen] = useState(false)
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({})
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const [originalWidth, setOriginalWidth] = useState<number | null>(null)

  useEffect(() => {
    if (open && buttonRef.current && portalRef.current) {
      // Calculate width only once when the dropdown opens
      const calculateWidth = () => {
        if (!containerRef.current) return

        // Only set width once when opening
        if (originalWidth === null && autoWidth) {
          const contentWidth = containerRef.current.offsetWidth
          setOriginalWidth(Math.max(minWidth, contentWidth))
        }
      }

      calculateWidth()

      const updatePosition = () => {
        if (!buttonRef.current || !portalRef.current) return

        // Use the stored width instead of recalculating
        const dropdownWidth = autoWidth ? originalWidth || minWidth : minWidth

        const buttonRect = buttonRef.current.getBoundingClientRect()
        const menuRect = portalRef.current.getBoundingClientRect()
        let isBottomPlacement = true
        if (verticalPlacement) {
          isBottomPlacement = verticalPlacement === "bottom"
        } else {
          if (
            menuRect.height !== undefined &&
            buttonRect.top + buttonRect.height + 10 + menuRect.height > window.innerHeight
          ) {
            isBottomPlacement = false
          }
        }

        const styles = {
          top: isBottomPlacement ? buttonRect.top + buttonRect.height + 10 : buttonRect.top - menuRect.height - 10,
          width: dropdownWidth,
          left:
            !placement || placement === "right"
              ? buttonRect.left - dropdownWidth + buttonRect.width
              : placement === "left"
                ? buttonRect.left
                : undefined,
          zIndex: 1000
        }

        setDropdownStyles(styles)
      }

      updatePosition()
      window.addEventListener("scroll", updatePosition, true)
      window.addEventListener("resize", updatePosition)
      return () => {
        window.removeEventListener("scroll", updatePosition, true)
        window.removeEventListener("resize", updatePosition)
      }
    }
  }, [autoWidth, open, placement, verticalPlacement, minWidth, originalWidth])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node) && !portalRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleToggle = () => {
    setOpen((prev) => !prev)
  }

  const handleOptionClick = (
    e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>,
    onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>) => void,
  ) => {
    if (preventCloseOnClick) {
      e.preventDefault()
      e.stopPropagation()
    } else {
      setOpen(false)
    }
    if (onClick) onClick(e)
  }

  return (
    <div className="flex h-full items-center">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={classNames(
          disabled ? "pointer-events-none opacity-30" : "cursor-pointer",
          "inline-flex justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
          "h-[35px]",
        )}
        data-testid={dataTestId}
      >
        {children}
      </button>
      {open &&
        createPortal(
          <div
            ref={portalRef}
            style={dropdownStyles}
            className={classNames(
              "absolute divide-y divide-gray-100 rounded-lg bg-white shadow-lg ring-1 ring-gray-200 ring-opacity-50 focus:outline-none p-0.5",
              zIndex ? zIndex : "z-50",
            )}
          >
            <div ref={containerRef} className="px-1 py-1">
              {options.map((option, index) =>
                option.label === "_divider_" ? (
                  <div className="my-1 border-b border-gray-200" key={index} />
                ) : (
                  <div
                    key={index}
                    onClick={(e) => handleOptionClick(e, option.onClick)}
                    className={classNames(
                      option.disabled
                        ? "text-gray-300 pointer-events-none"
                        : "hover:bg-gray-100 text-muted hover:cursor-pointer transition-colors duration-75",
                      "group flex w-full items-center rounded-md px-2 py-2 whitespace-nowrap transition-colors",
                    )}
                  >
                    {option.icon && (
                      <span className="mr-2">
                        <option.icon className="w-4 h-4" />
                      </span>
                    )}
                    <span className="w-full group-hover:text-black text-gray-600">{option.label}</span>
                  </div>
                ),
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default DropdownButton
